import { withShareUtm } from '@goaltree/backend/convex/lib/shareUtm'
import {
  SOCIAL_GLYPHS,
  SOCIAL_NAMES,
  SOCIAL_NETWORKS,
  type SocialNetwork,
  shareIntent,
} from '@goaltree/ui/lib/social-share'
import { Check, Share2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { canNativeShare, nativeShare, openIntent } from '@/lib/share'

export function SocialGlyph({
  network,
  className,
}: {
  network: SocialNetwork
  className?: string
}) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d={SOCIAL_GLYPHS[network]} />
    </svg>
  )
}

// One-tap share row: each network's own composer (no auto-posting), plus the OS
// share sheet where the browser has one (mobile web — reaches Instagram etc.).
export function ShareButtons({
  text,
  url,
  className,
}: {
  text: string
  url: string
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <div className={className}>
      {SOCIAL_NETWORKS.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => openIntent(shareIntent(n, text, url))}
          aria-label={t('postComposer.shareOn', { network: SOCIAL_NAMES[n] })}
          title={SOCIAL_NAMES[n]}
          className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full opacity-80 transition-opacity hover:opacity-100"
        >
          <SocialGlyph network={n} className="h-3.5 w-3.5" />
        </button>
      ))}
      {canNativeShare() && (
        <button
          type="button"
          onClick={() => void nativeShare({ text, url })}
          className="inline-flex h-7 cursor-pointer items-center gap-1 rounded-full px-2 text-xs font-semibold opacity-80 hover:opacity-100"
        >
          <Share2 className="h-3.5 w-3.5" />
          {t('postComposer.more')}
        </button>
      )}
    </div>
  )
}

export function PostedToast({
  text,
  url,
  onDismiss,
}: {
  text: string
  url: string
  onDismiss: () => void
}) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(withShareUtm(url, 'post_link'))
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error(t('share.copyFailed', { defaultValue: 'Could not copy' }))
    }
  }

  return (
    <div className="flex w-[min(420px,calc(100vw-32px))] flex-col gap-1.5 rounded-xl bg-foreground px-3.5 py-2.5 text-[13px] font-semibold text-background shadow-xl">
      <div className="flex items-center gap-3">
        <Check
          className="h-3.5 w-3.5 shrink-0 [color:color-mix(in_srgb,#7ed957_60%,var(--background))]"
          strokeWidth={3}
        />
        <span className="min-w-0 truncate">{t('postComposer.posted')}</span>
        <span className="flex-1" />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onDismiss}
          className="font-extrabold [color:color-mix(in_srgb,var(--primary)_55%,var(--background))] hover:underline"
        >
          {t('postComposer.view')}
        </a>
        <button
          type="button"
          onClick={() => void copy()}
          className="cursor-pointer font-extrabold [color:color-mix(in_srgb,var(--primary)_55%,var(--background))] hover:underline"
        >
          {copied ? t('share.copied') : t('postComposer.copyLink')}
        </button>
      </div>
      <ShareButtons text={text} url={url} className="-mx-1.5 flex items-center gap-0.5" />
    </div>
  )
}
